import { useEffect, useMemo, useState, useRef } from 'react';
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
    type MRT_TableOptions,
} from 'material-react-table';
import { Chip, Pagination, Typography, TextField, Button, Box, InputAdornment, useMediaQuery, Menu, MenuItem, PaginationItem, IconButton } from '@mui/material';
import searchIcon from 'packages/novous_web/src/assets/icons/Icon.svg'
import filterIcon from 'packages/novous_web/src/assets/icons/Filter.svg'
import Custom from 'packages/novous_web/src/assets/icons/CustomBlue.svg'
import downloadWhiteIcon from 'packages/novous_web/src/assets/icons/downloadWhiteIcon.svg'
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import { useSidebar } from "packages/novous_web/src/context/SideBarContext";
import arrow from "packages/novous_web/src/assets/icons/Arrow.svg";
import calendar from "packages/novous_web/src/assets/icons/calendar.svg";
import DateRangeFilter from "../../../../common/components/DateRange";
import dayjs from "dayjs";
import { ArrowBack, MiscellaneousServices } from '@mui/icons-material';
import { useNavigation } from 'react-router-dom';
import CustomiseTableModal from 'packages/novous_web/src/common/components/CustomiseTable';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import useApiError from 'packages/novous_web/src/api/useApiError';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import Clear from 'packages/novous_web/src/assets/icons/Refresh.svg';
import CreateNewDocument from './components/CreateNewDocument';
import useParamsAuth from 'packages/novous_web/src/api/useParamsAuth';
import PermissionGuard from 'packages/novous_web/src/common/components/PermissionGuard';
import { PermissionCategory, PermissionSubcategory } from 'packages/novous_web/src/utils/permissionConstants';
import eye from "packages/novous_web/src/assets/icons/Eye.svg";
import menu from "packages/novous_web/src/assets/icons/menu.svg";
import deleteIcon from 'packages/novous_web/src/assets/icons/Delete.svg';
import editIcon from 'packages/novous_web/src/assets/icons/edit.svg';
import DeleteDocumentData from './components/DeleteDocumentData';
import PreviewModal from './components/PreviewModal';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DocumentViewerModal from 'packages/novous_web/src/common/components/DocumentViewerModal';

interface CompanyData {
    id: string;
    name: string;
    description: string;
    type: string;
    crn: string;
    address: {
        fullAddress: string;
        city: string;
        state: string;
        pincode: number;
        country: string;
    };
    contacts: {
        telephoneNo: string;
        mobileNo: string;
        email: string;
    };
    url: string;
    status: number;
    deviceTypes: string[] | null;
}

interface DeviceHealthReport {
    attachments: string[];
    category: string;
    createdAt: string;
    createdByCategoryStatus: string;
    createdByDocumentType: string;
    createdByDocumentTypeStatus: string;
    createdByStatus: string;
    department: string;
    docIdNumber: string;
    documentType: string;
    documentUniqueId: string;
    entityId: string;
    expiryDate: string;
    fleetId: string;
    notes: string;
    sortKey: string;
    status: string;
    updatedAt: string;
}

const data: DeviceHealthReport[] = []

type ColumnObjType = {
    [key: string | number]: {
        name: string;
        status: boolean;
    };
};

export default function DocumentsTable() {
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>("");
    const [alertType, setAlertType] = useState<"error" | "success" | "warning" | "info">("error");
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const location = useLocation();
    const vehicleData = location.state || {};
    const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedFormat, setSelectedFormat] = useState<string>('pdf');
    const {
        data: downloadDeviceHealthReportData,
        error: downloadDeviceHealthReportError,
        loading: downloadDeviceHealthReportLoading,
        fetchData: fetchDownloadDeviceHealthReportData,
    } = useApiError<any>(`${API_ENDPOINTS.report.DEVICE_HEALTH_DOWNLOAD}`, "POST", 'blob');

    const handleDocumentSave = async () => {
        setIsSendingEmail(true);
        const requestData = {
            fleetId: user?.customer_id,
            fileType: selectedFormat,
            vehicleId: vehicleData?.vehicleRegistrationNo,
            startDate,
            endDate
        };

        try {
            const response = await fetchDownloadDeviceHealthReportData(requestData);
            if (response?.type === 'text/csv' || response?.type === 'application/pdf' || response?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
                setAlertMessage(`File has been downloaded successfully`);
                setAlertType("success");
                setAlertOpen(true);
                setSelectedDuration(dateRangeArr[0]);
            } else {
                setAlertMessage(response?.error);
                setAlertType("error");
                setAlertOpen(true);
            }
        } catch (error) {
            console.error("Error sending email:", error);
            setAlertMessage((error as Error).message);
            setAlertType("error");
            setAlertOpen(true);
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedFormat(event.target.value);
    };

    useEffect(() => {
        if (downloadDeviceHealthReportData) {
            try {
                if (downloadDeviceHealthReportData instanceof Blob) {
                    const url = window.URL.createObjectURL(downloadDeviceHealthReportData);
                    const a = document.createElement('a');
                    a.href = url;
                    let extension = selectedFormat.toLowerCase();
                    if (extension === 'excel') {
                        extension = 'xlsx';
                    }

                    const fileName = `daily_device_health_report.${extension}`;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                } else {
                    console.error('Invalid response format. Expected a Blob.');
                }
            } catch (error) {
                console.error('Error downloading file:', error);
            } finally {
                setIsDocumentModalOpen(false);
            }
        }
    }, [downloadDeviceHealthReportData])

    const handleDocumentModalClose = () => {
        if (downloadDeviceHealthReportLoading) {
            return;
        }
        setIsDocumentModalOpen(false);
        setEditDocumentData(null);
    };

    const handleDeleteModalClose = () => {
        if (downloadDeviceHealthReportLoading) {
            return;
        }
        setIsDeleteModalOpen(false);
        setDeleteDocumentData(null);
    };

    const navigate = useNavigate();
    const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);

    const dateRangeArr = [
        "Today",
        "Yesterday",
        "7 Days",
        "15 Days",
        "30 Days",
        "Custom Range",
    ];

    const [selectedDuration, setSelectedDuration] = useState(dateRangeArr[0]);
    const [calendarAnchorEl, setCalendarAnchorEl] = useState<null | HTMLElement>(
        null
    );
    const [mapData, setMapData] = useState([]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const insightsRef = useRef<HTMLDivElement>(null);
    const [insightsWidth, setInsightsWidth] = useState<number | null>(null);
    const [startDate, setStartDate] = useState("");
    const { isSideBarOpen } = useSidebar();
    const [endDate, setEndDate] = useState("");
    const paginatedArr = useRef<(string | null)[]>([null]);

    const handleClosed = () => setIsCustomizeOpen(false);

    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedVehicle, setSelectedVehicle] = useState('Select Vehicle');
    const [selectedDriver, setSelectedDriver] = useState('Select Driver');
    const [selectedUser, setSelectedUser] = useState('Select User');
    const [selectedCompany, setSelectedCompany] = useState('Select Company');
    const [selectedDocument, setSelectedDocument] = useState('Select Document Type');
    const [selectedStatus, setSelectedStatus] = useState('Select Status');
    const { user } = useAuth();
    const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
    const [showDriverDropdown, setShowDriverDropdown] = useState(false);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [showDocumentDropdown, setShowDocumentDropdown] = useState(false);
    const [vehiclesList, setVehiclesList] = useState<any[]>([]);
    const [driverssList, setDriverssList] = useState<any[]>([]);
    const [usersList, setUsersList] = useState<any[]>([]);
    const [companyLists, setCompanyLists] = useState<any[]>([]);
    const isSuperAdmin = user?.authorities?.includes('ROLE_SUPER_ADMIN');

    const {
        data: DriversListData,
        error: DriversListError,
        loading: DriversListLoading,
        fetchData: fetchDriversListData,
    } = useAxios<any>(
        `${API_ENDPOINTS.directories.GET_ALL_DRIVER}`,
        "POST"
    );

    const {
        data: VehiclesListData,
        error: VehiclesListError,
        loading: VehiclesListLoading,
        fetchData: VehiclesListRequestData,
    } = useAxios<any>(API_ENDPOINTS.directories.GET_ALL_VEHICLES, "POST"
    );

    const {
        data: companyList,
        error: companyListError,
        loading: companyListLoading,
        fetchData: fetchCompanyListData,
    } = useAxios<any>(`${API_ENDPOINTS.users.COMPANY_LIST}`, "GET");

    useEffect(() => {
        fetchCompanyListData();
    }, []);

    const {
        data: companyListByCrn,
        error: companyListByCrnError,
        loading: companyListByCrnLoading,
        fetchData: fetchCompanyListByCrnData,
    } = useAxios<any>(`${API_ENDPOINTS.users.CUSTOMER_LIST_CRN}`, "POST");

    const {
        data: DocumentCategory,
        error: DocumentCategoryError,
        loading: DocumentCategoryLoading,
        fetchData: fetchDocumentCategoryData,
    } = useAxios<any>(`${API_ENDPOINTS.documents.DOCUMENT_CATEGORY}`, "GET");

    const {
        data: DocumentsTypes,
        error: DocumentsTypesError,
        loading: DocumentsTypesLoading,
        fetchData: fetchDocumentsTypesData,
    } = useParamsAuth<any>(`${API_ENDPOINTS.documents.DOCUMENTS_TYPES}`, "GET");

    useEffect(() => {
        if (selectedCategory && selectedCategory !== 'All') {
            fetchDocumentsTypesData({
                params: {
                    category: selectedCategory
                }
            });
            setSelectedDocument('Select Document Type');
        }
    }, [selectedCategory]);

    useEffect(() => {
        fetchDocumentCategoryData();
    }, [])

    useEffect(() => {
        VehiclesListRequestData({ fleetId: user?.customer_id, lastEvaluatedKey: null });
    }, [user?.customer_id]);

    useEffect(() => {
        fetchDriversListData({ fleetId: user?.customer_id });
    }, [user?.customer_id]);

    useEffect(() => {
        fetchCompanyListByCrnData({ userCrn: user?.crn, userId: user?.id })
    }, [])

    useEffect(() => {
        if (DriversListData?.data?.drivers) {
            const driverListData = DriversListData.data.drivers.map(
                (driver: any) => ({
                    empId: driver?.empId ?? "NA",
                    driverName: driver?.driverName ?? "NA",
                    driverId: driver?.driverId ?? "NA",
                    driverScore: driver?.driverScore ?? 0,
                    dateOfJoining: driver?.dateOfJoining ?? "NA",
                    totalDistanceDriven: driver?.totalDistanceDriven ?? 0,
                    vehicleRegNo: driver?.vehicleLists ?? ["NA"],
                    phone: driver?.phoneNumber ?? "NA",
                    license: driver?.licId ?? "NA",
                })
            );

            setDriverssList(driverListData);
        } else {
            setDriverssList([]);
        }
    }, [DriversListData]);

    useEffect(() => {
        if (companyListByCrn?.data) {
            const companyListByCrnData = companyListByCrn.data.map(
                (user: any) => ({
                    username: user.username,
                    crn: user.crn,
                    authorities: user.authorities,
                    id: user.id
                })
            );

            setUsersList(companyListByCrnData);
        } else {
            setUsersList([]);
        }
    }, [companyListByCrn]);

    useEffect(() => {
        if (companyList?.data) {
            const companyListData = companyList.data.map(
                (user: any) => {
                    return {
                        username: user.name,
                        crn: user.crn,
                        userType: user.type,
                        id: user.id
                    }
                }
            );

            setCompanyLists(companyListData);
        } else {
            setCompanyLists([]);
        }
    }, [companyList])

    useEffect(() => {
        if (VehiclesListData?.data) {
            const vehicleListData = VehiclesListData?.data?.map((vehicle: any) => ({
                vehicleName: vehicle.vehicleName,
                driverName: vehicle.driverName,
                gpsSpeed: vehicle.gpsSpeed,
                vehicleRegistrationNo: vehicle.vehicleRegistrationNo,
                vehicleType: vehicle.vehicleType,
                lastLocationAddress: vehicle.lastLocationAddress,
                vehicleState: vehicle.vehicleState,
                lastLatitude: vehicle.lastLatitude,
                lastLongitude: vehicle.lastLongitude,
                chassisNo: vehicle.chassisNo,
                deviceType: vehicle.deviceType,
                modelYear: vehicle.modelYear,
                model: vehicle.model,
                vehicleManufacturer: vehicle.vehicleManufacturer,
                groupName: vehicle.groupName,
                lastUpdateTime: vehicle.lastUpdateTime,
                deviceId: vehicle.deviceId,
                tripStatus: vehicle.tripStatus,
                totalKmToday: vehicle.totalKmToday,
                tripName: vehicle.tripName,
                tripId: vehicle.tripId,
            }));
            setVehiclesList(vehicleListData)
        }
    }, [VehiclesListData])

    const baseCategoryOptions = useMemo(() => {
        if (!DocumentCategory) return [];

        const options = DocumentCategory.map((category: string) => ({
            label: category.charAt(0).toUpperCase() + category.slice(1).toLowerCase(),
            value: category
        }));

        return [{ label: "All", value: "All" }, ...options];
    }, [DocumentCategory]);

    const CategoryOptions = useMemo(() => {
        return isSuperAdmin
            ? baseCategoryOptions
            : baseCategoryOptions.filter(option => option.value !== 'USER');
    }, [isSuperAdmin, baseCategoryOptions]);

    const VehiclesData = useMemo(() => {
        return vehiclesList?.map(vehicle => ({
            label: vehicle.vehicleRegistrationNo || 'Unknown',
            value: vehicle.vehicleRegistrationNo || 'Unknown'
        })) || []
    }, [vehiclesList]);

    const DriversData = useMemo(() => {
        return driverssList?.map((driver) => ({
            label: driver.driverName || 'Unknown',
            value: driver.driverId || 'Unknown'
        })) || [];
    }, [driverssList]);

    const UsersData = useMemo(() => {
        return usersList?.map((user) => ({
            label: user.username || 'Unknown',
            value: user.id || 'Unknown'
        })) || [];
    }, [usersList]);

    const CompanyData = useMemo(() => {
        return companyLists?.map((user) => ({
            label: user.username || 'Unknown',
            value: user.id || 'Unknown'
        })) || [];
    }, [companyLists]);

    const Status = [
        {
            label: 'Active',
            value: 'ACTIVE'
        },
        {
            label: 'Expired',
            value: 'EXPIRED'
        }
    ]

    const handleClear = () => {
        setSelectedCategory('All');
        setSelectedVehicle('Select Vehicle');
        setSelectedDriver('Select Driver');
        setSelectedUser('Select User');
        setSelectedCompany('Select Company');
        setSelectedDocument('Select Document Type');
        setSelectedStatus('Select Status');

        setShowVehicleDropdown(false);
        setShowDriverDropdown(false);
        setShowUserDropdown(false);
        setShowCompanyDropdown(false);
        setShowDocumentDropdown(false);
    };

    const filteredDocumentOptions = useMemo(() => {
        if (selectedCategory === 'All') return [];

        if (DocumentsTypes?.documentTypes) {
            return DocumentsTypes.documentTypes.map((type: any) => ({
                label: type,
                value: type
            }));
        }

        return [];
    }, [DocumentsTypes, selectedCategory]);

    const handleCategoryChange = (value: string) => {
        setSelectedCategory(value);
        setSelectedVehicle('Select Vehicle');
        setSelectedDriver('Select Driver');
        setSelectedUser('Select User');
        setSelectedCompany('Select Company');
        setSelectedDocument('Select Document Type');
        setSelectedStatus('Select Status');

        setShowVehicleDropdown(value === 'VEHICLE');
        setShowDriverDropdown(value === 'DRIVER');
        setShowCompanyDropdown(value === 'COMPANY');
        setShowUserDropdown(value === 'USER' && isSuperAdmin);

        setShowDocumentDropdown(value !== 'All');
    };

    const handleDocumentChange = (value: string) => {
        setSelectedDocument(value);
    };

    const [columnObj, setColumnObj] = useState<ColumnObjType>({
        entityId: { name: "Entity Details", status: true },
        category: { name: "Category", status: true },
        documentType: { name: "Document Type", status: true },
        createdAt: { name: "created On", status: true },
        expiryDate: { name: "Expiring On", status: true },
        status: { name: "Status", status: true },
        attachments: { name: "Documents Images", status: true },
        actions: { name: "Actions", status: true },
    });

    const handelCustomize = () => {
        setIsCustomizeOpen(!isCustomizeOpen);
    };
    const handleReset = () => {
        console.log('Reset clicked!');
    };

    const getDateRange = (range: string) => {
        const today = dayjs();
        let startDate, endDate;

        switch (range) {
            case "Today":
                startDate = today.format("YYYY-MM-DD");
                endDate = today.format("YYYY-MM-DD");
                break;
            case "Yesterday":
                startDate = dayjs().subtract(1, "day").format("YYYY-MM-DD");
                endDate = startDate;
                break;
            case "7 Days":
                startDate = dayjs().subtract(6, "day").format("YYYY-MM-DD");
                endDate = today.format("YYYY-MM-DD");
                break;
            case "15 Days":
                startDate = dayjs().subtract(14, "day").format("YYYY-MM-DD");
                endDate = today.format("YYYY-MM-DD");
                break;
            case "30 Days":
                startDate = dayjs().subtract(29, "day").format("YYYY-MM-DD");
                endDate = today.format("YYYY-MM-DD");
                break;
            default:
                startDate = "";
                endDate = "";
        }
        return { startDate, endDate };
    };

    let { startDate: startDateC, endDate: endDateC } =
        getDateRange(selectedDuration);

    useEffect(() => {

        if (insightsRef.current) {
            console.log('   insig', insightsRef.current.offsetWidth);

            setInsightsWidth(insightsRef.current.offsetWidth);
        }
    }, [isSideBarOpen]);

    useEffect(() => {
        if (startDateC !== "" && endDateC !== "") {
        }
    }, [startDateC, endDateC]);

    if (startDate !== "" && endDate !== "") {
        startDateC = startDate;
        endDateC = endDate;
    } else if (startDate === "" && endDate === "") {
        ({ startDate: startDateC, endDate: endDateC } =
            getDateRange(selectedDuration));
    }

    useEffect(() => {
        const { startDate: newStartDate, endDate: newEndDate } = getDateRange(selectedDuration);
        setStartDate(newStartDate);
        setEndDate(newEndDate);
    }, [selectedDuration]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        setCalendarAnchorEl(event.currentTarget);
    };

    const handleCalendarClose = () => {
        setShowDatePicker(false);
        // setStartDate("");
        // setEndDate("");

        // setSelectedDuration(dateRangeArr[0]);
        setCalendarAnchorEl(null);
    };

    const handleClose = (value?: string) => {
        if (value) {
            if (selectedDuration === "Custom Range" && value !== "Custom Range") {
                const { startDate: newStartDate, endDate: newEndDate } = getDateRange(value);
                setStartDate(newStartDate);
                setEndDate(newEndDate);
            }

            setSelectedDuration(value);
            setShowDatePicker(value === "Custom Range");
            if (value === "Custom Range") {
                setCalendarAnchorEl(anchorEl);
            }
        }

        setAnchorEl(null);
    };

    const [currentPage, setCurrentPage] = useState(1);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isSmallScreen = useMediaQuery("(max-width:850px)");

    interface DeviceHealthReportResponse {
        documents: DeviceHealthReport[];
        lastEvaluatedKey: string | null;
    }

    const {
        data: DeviceHealthReportData,
        error: DeviceHealthReportError,
        loading: DeviceHealthReportLoading,
        fetchData: DeviceHealthReportRequestData,
    } = useAxios<DeviceHealthReportResponse>(API_ENDPOINTS.documents.DOCUMENTS_LISTING, "POST"
    );

    const {
        data: DocumentOpenModalData,
        error: DocumentOpenModalError,
        loading: DocumentOpenModalLoading,
        fetchData: DocumentOpenModalRequestData,
    } = useApiError<any>(API_ENDPOINTS.documents.DOCUMENT_MODAL_URL, "POST", 'blob');

    useEffect(() => {
        if (DeviceHealthReportData?.lastEvaluatedKey) {
            if (!paginatedArr.current.includes(DeviceHealthReportData.lastEvaluatedKey)) {
                paginatedArr.current.push(DeviceHealthReportData.lastEvaluatedKey);
            }
        }
    }, [DeviceHealthReportData]);

    const totalPages = DeviceHealthReportData?.lastEvaluatedKey ? paginatedArr.current.length + 1 : paginatedArr.current.length;

    const handlePageChange = (
        event: React.ChangeEvent<unknown>,
        page: number
    ) => {
        if (page > currentPage && !DeviceHealthReportData?.lastEvaluatedKey) return;
        setCurrentPage(page);

        // DeviceHealthReportRequestData({
        //     startDate: startDate || startDateC,
        //     endDate: endDate || endDateC,
        //     fleetId: user.customer_id,
        //     lastEvaluatedKey: page === 1 ? null : paginatedArr.current[page - 1],
        // });
        if (startDate && endDate) {
            const payload: any = {
                startDate: startDate || startDateC,
                endDate: endDate || endDateC,
                fleetId: user.customer_id,
                lastEvaluatedKey: page === 1 ? null : paginatedArr.current[page - 1],
            };

            if (selectedCategory !== 'All') {
                payload.category = selectedCategory;
            }

            if (selectedVehicle !== 'All' && selectedVehicle !== 'Select Vehicle' && selectedCategory === 'VEHICLE') {
                payload.entityId = selectedVehicle;
            }

            if (selectedDriver !== 'All' && selectedCategory === 'DRIVER') {
                payload.entityId = selectedDriver;
            }

            if (selectedCompany !== 'All' && selectedCategory === 'COMPANY') {
                payload.entityId = selectedCompany;
            }

            if (selectedUser !== 'All' && selectedCategory === 'USER' && isSuperAdmin) {
                payload.entityId = selectedUser;
            }

            if (selectedDocument !== 'All' && selectedDocument !== 'Select Document Type') {
                payload.documentType = selectedDocument;
            }

            if (selectedStatus !== '' && selectedStatus !== 'Select Status') {
                payload.status = selectedStatus;
            }

            // setCurrentPage(1);
            // paginatedArr.current = [null];
            DeviceHealthReportRequestData(payload);
        }
    };

    useEffect(() => {
        if (startDate && endDate) {
            const payload: any = {
                startDate: startDate || startDateC,
                endDate: endDate || endDateC,
                fleetId: user.customer_id,
                lastEvaluatedKey: null,
            };

            if (selectedCategory !== 'All') {
                payload.category = selectedCategory;
            }

            if (selectedVehicle !== 'All' && selectedVehicle !== 'Select Vehicle' && selectedCategory === 'VEHICLE') {
                payload.entityId = selectedVehicle;
            }

            if (selectedDriver !== 'All' && selectedCategory === 'DRIVER') {
                payload.entityId = selectedDriver;
            }

            if (selectedCompany !== 'All' && selectedCategory === 'COMPANY') {
                payload.entityId = selectedCompany;
            }

            if (selectedUser !== 'All' && selectedCategory === 'USER' && isSuperAdmin) {
                payload.entityId = selectedUser;
            }

            if (selectedDocument !== 'All' && selectedDocument !== 'Select Document Type') {
                payload.documentType = selectedDocument;
            }

            if (selectedStatus !== '' && selectedStatus !== 'Select Status') {
                payload.status = selectedStatus;
            }

            setCurrentPage(1);
            paginatedArr.current = [null];
            DeviceHealthReportRequestData(payload);
        }
    }, [selectedCategory, selectedVehicle, selectedCompany, selectedDriver, selectedUser, selectedDocument, selectedStatus, startDate, endDate, user.customer_id]);

    const processedData = useMemo(() => {
        if (!DeviceHealthReportData?.documents) return [];
        return (DeviceHealthReportData as DeviceHealthReportResponse)?.documents?.map((report) => ({
            ...report,
            totalEventsCount:
                (report.entityId ?? 0)
        }));
    }, [DeviceHealthReportData]);

    const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(
        null
    );

    const [selectedTrip, setSelectedTrip] = useState<any>(null);
    const [editDocumentData, setEditDocumentData] = useState<DeviceHealthReport | null>(null);
    const [deleteDocumentData, setDeleteDocumentData] = useState<DeviceHealthReport | null>(null);
    const handleOpenMenu = (
        event: React.MouseEvent<HTMLSpanElement>,
        trip: any
    ) => {
        setRowMenuAnchorEl(event.currentTarget);
        setSelectedTrip(trip);
    };

    const handleCloseMenu = () => {
        setRowMenuAnchorEl(null);
        setSelectedTrip(null);
    };

    const handelEdit = (rowData: any) => {
        setEditDocumentData(rowData);
        setIsDocumentModalOpen(true);
        handleCloseMenu();
    }

    const handelDelete = (rowData: any) => {
        setDeleteDocumentData(rowData);
        setIsDeleteModalOpen(true);
        handleCloseMenu();
    }

    const viewMenuData = [
        {
            name: "Delete",
            icon: deleteIcon,
            click: () => handelDelete(selectedTrip)
        },
        {
            name: "Edit",
            icon: editIcon,
            click: () => handelEdit(selectedTrip)
        },
    ];

    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [currentPreviewUrl, setCurrentPreviewUrl] = useState<string>('');
    const [currentFileType, setCurrentFileType] = useState<'image' | 'pdf' | 'other'>('other');

    // useEffect(() => {
    //     const handleContextMenu = (e: Event) => {
    //         e.preventDefault();
    //     };

    //     document.addEventListener('contextmenu', handleContextMenu);

    //     return () => {
    //         document.removeEventListener('contextmenu', handleContextMenu);
    //     };
    // }, []);

    const [currentOriginalUrl, setCurrentOriginalUrl] = useState('');

    const handlePreview = async (url: string, fileName?: string) => {
        try {
            console.log('Original URL:', url);
            console.log('File name:', fileName);

            const fileExtension = url.split('.').pop()?.toLowerCase().split('?')[0] || '';
            const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileExtension);
            const isPDF = fileExtension === 'pdf';

            setCurrentFileType(isImage ? 'image' : isPDF ? 'pdf' : 'other');

            const extractedFileName = fileName || url.split('/').pop() || 'document';
            setCurrentOriginalUrl(extractedFileName);

            console.log('Calling DocumentOpenModalRequestData with attachment:', url);

            const response = await DocumentOpenModalRequestData({
                attachment: url
            });

            console.log('API Response type:', typeof response);
            console.log('API Response:', response);

            if (response instanceof Blob) {
                // Create object URL from the blob
                const objectUrl = URL.createObjectURL(response);
                console.log('Created object URL:', objectUrl);

                setCurrentPreviewUrl(objectUrl);
                setPreviewModalOpen(true);
            } else if (response?.data instanceof Blob) {
                // If response.data is the blob
                const objectUrl = URL.createObjectURL(response.data);
                console.log('Created object URL from response.data:', objectUrl);

                setCurrentPreviewUrl(objectUrl);
                setPreviewModalOpen(true);
            } else {
                throw new Error('Invalid response format - expected Blob');
            }

        } catch (error) {
            console.error('Error opening document:', error);
            setAlertMessage('Failed to open document: ' + (error as Error).message);
            setAlertType('error');
            setAlertOpen(true);
        }
    };

    const allColumns: MRT_ColumnDef<DeviceHealthReport>[] = useMemo(
        () => [
            {
                accessorKey: 'entityId',
                header: 'Entity Details',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => <Typography variant="body2" fontWeight={400} noWrap>{cell.getValue<string>()}</Typography>,
            },
            {
                accessorKey: 'category',
                header: 'Category',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => <Typography variant="body2" fontWeight={400} noWrap>{cell.getValue<string>()}</Typography>,
            },
            {
                accessorKey: 'documentType',
                header: 'Document Type',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => <Typography variant="body2" fontWeight={400} noWrap>{cell.getValue<string>()}</Typography>,
            },
            {
                accessorKey: 'createdAt',
                header: 'Created On',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => {
                    const rawValue = cell.getValue<string>();
                    const date = new Date(rawValue);
                    const formatted = date.toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                    });
                    return <Typography variant="body2" fontWeight={400} noWrap>{formatted}</Typography>;
                },
            },
            {
                accessorKey: 'expiryDate',
                header: 'Expiring On',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => {
                    const rawValue = cell.getValue<string>();
                    const date = new Date(rawValue);
                    const formatted = date.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                    });
                    return <Typography variant="body2" fontWeight={400} noWrap>{formatted}</Typography>;
                },
            },
            {
                accessorKey: 'status',
                header: 'Status',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => <Typography variant="body2" fontWeight={400} noWrap>{cell.getValue<string>()}</Typography>,
            },
            {
                accessorKey: 'attachments',
                header: 'Document Images',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell, row }) => {
                    const files = cell.getValue<string[]>();

                    if (!files?.length) return null;

                    return (
                        <Box display="flex" flexDirection="row" gap={1} flexWrap="wrap">
                            {files.map((url, index) => {
                                const fileExtension = url.split('.').pop()?.toLowerCase().split('?')[0] || '';
                                const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileExtension);
                                const isPDF = fileExtension === 'pdf';

                                const clickableStyle = {
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    }
                                };

                                if (isImage) {
                                    return (
                                        <ImageIcon
                                            key={index}
                                            onClick={() => handlePreview(url)}
                                            sx={clickableStyle}
                                        />
                                    );
                                }

                                if (isPDF) {
                                    return (
                                        <PictureAsPdfIcon
                                            key={index}
                                            onClick={() => handlePreview(url)}
                                            sx={clickableStyle}
                                        />
                                    );
                                }

                                return null;
                            })}
                        </Box>
                    );
                },
            },
            {
                accessorKey: "actions",
                header: "Actions",
                muiTableBodyCellProps: { sx: { padding: "8px" } },
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 180,
                Cell: ({ row }) => (

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <IconButton
                            sx={{
                                padding: 1,
                                "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (row.original) {
                                    handleOpenMenu(event, row.original);
                                } else {
                                    console.error("Row data is undefined");
                                }
                            }}
                        >
                            <img src={menu} alt="menu" width={6} height={24} />
                        </IconButton>
                    </Box>
                ),
            },
        ],
        [isSmallScreen]
    );

    const columns: MRT_ColumnDef<DeviceHealthReport>[] = useMemo(() => {
        return allColumns.filter((column) => {
            const key = column.accessorKey as keyof ColumnObjType;
            return columnObj[key]?.status;
        });
    }, [allColumns, columnObj]);

    // Table options
    const tableOptions: MRT_TableOptions<DeviceHealthReport> = {
        columns,
        data: processedData,
        enablePagination: false,
        enableColumnFilters: false,
        enableSorting: false,
        enableGlobalFilter: false,
        enableDensityToggle: false,
        enableHiding: false,
        enableBottomToolbar: false,
        enableTopToolbar: false,
        enableFullScreenToggle: false,
        enableColumnResizing: false,
        enableToolbarInternalActions: false,
        // initialState: {
        //     columnPinning: {
        //         left: [], // Fix these columns to the left
        //         right: [], // No pinned columns on the right
        //     },
        // },
        muiTableBodyProps: {
            sx: {
                padding: 0,
                margin: 0,
            },
        },
    };

    const table = useMaterialReactTable(tableOptions);


    return (
        <>
            <AlertToast message={alertMessage} type={alertType} open={alertOpen} onClose={() => setAlertOpen(false)} />
            {/* <PreviewModal
                open={previewModalOpen}
                onClose={() => setPreviewModalOpen(false)}
                url={currentPreviewUrl}
                fileType={currentFileType}
            /> */}
            {previewModalOpen && <DocumentViewerModal open={previewModalOpen} onClose={() => setPreviewModalOpen(false)} fileName={currentPreviewUrl} imageName={currentOriginalUrl} fileType={currentFileType} />}
            <div className='flex flex-col gap-4 w-full'>
                {/* Search Input */}
                <div className="w-full">
                    <button className="text-blue-600 font-medium flex items-center" onClick={() => navigate(-1)}>
                        <ArrowBack className="mr-1" /> Back
                    </button>
                </div>
                <div className=' w-full flex flex-col justify-between gap-4 items-start'>
                    <div className="w-full flex items-center gap-4">
                        <div className="dashboard-header-left">
                            <div className="flex flex-col items-start">
                                <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px'>
                                    Documents
                                </Typography>
                            </div>
                        </div>
                        <PermissionGuard
                            category={PermissionCategory.DOCUMENTS}
                            subcategory={PermissionSubcategory.SHOW_DOCUMENT_FILTER}
                        >
                            <div className='grid grid-cols-5 gap-4 w-full items-center'>
                                <CustomDropDownInput
                                    placeholder="Select Category"
                                    options={CategoryOptions}
                                    value={selectedCategory}
                                    onChange={handleCategoryChange}
                                    showDropdownArrow={true}
                                />
                                {showVehicleDropdown && (
                                    <CustomDropDownInput
                                        placeholder="Select Vehicle"
                                        options={VehiclesData}
                                        value={selectedVehicle}
                                        onChange={(value) => setSelectedVehicle(value)}
                                        showDropdownArrow={true}
                                    />
                                )}
                                {showDriverDropdown && (
                                    <CustomDropDownInput
                                        placeholder="Select Driver"
                                        options={DriversData}
                                        value={selectedDriver}
                                        onChange={(value) => setSelectedDriver(value)}
                                        showDropdownArrow={true}
                                    />
                                )}
                                {showUserDropdown && isSuperAdmin && (
                                    <CustomDropDownInput
                                        placeholder="Select Users"
                                        options={UsersData}
                                        value={selectedUser}
                                        onChange={(value) => setSelectedUser(value)}
                                        showDropdownArrow={true}
                                    />
                                )}
                                {showCompanyDropdown && (
                                    <CustomDropDownInput
                                        placeholder="Select Company"
                                        options={CompanyData}
                                        value={selectedCompany}
                                        onChange={(value) => setSelectedCompany(value)}
                                        showDropdownArrow={true}
                                    />
                                )}
                                {showDocumentDropdown && (
                                    <CustomDropDownInput
                                        placeholder="Select Document Type"
                                        options={filteredDocumentOptions}
                                        value={selectedDocument}
                                        onChange={handleDocumentChange}
                                        showDropdownArrow={true}
                                    />
                                )}
                                <CustomDropDownInput
                                    placeholder="Select Status"
                                    options={Status}
                                    value={selectedStatus}
                                    onChange={(value) => setSelectedStatus(value)}
                                    showDropdownArrow={true}
                                />
                                {(selectedCategory !== 'All' ||
                                    selectedVehicle !== 'Select Vehicle' ||
                                    selectedDriver !== 'Select Driver' ||
                                    isSuperAdmin && selectedUser !== 'Select User' ||
                                    selectedCompany !== 'Select Company' ||
                                    selectedDocument !== 'Select Document Type' ||
                                    selectedStatus !== 'Select Status') && (
                                        <CustomIconButton
                                            text="Clear All"
                                            icon={<img src={Clear} alt="reset" />}
                                            background="transparent"
                                            variant="outlined"
                                            onClick={handleClear}
                                        />
                                    )}
                            </div>
                        </PermissionGuard>
                    </div>
                    <div className="flex gap-4 w-full justify-end">
                        <CustomIconButton
                            icon={<img src={Custom} alt="Custom" width={16} height={16} />}
                            text="Customize"
                            background='transparent'
                            outline={false} // Set to true if you want an outline
                            onClick={handelCustomize}
                        />
                        <CustomiseTableModal
                            open={isCustomizeOpen}
                            onClose={handleClosed}
                            onReset={handleReset}
                            title="Customize Your Table"
                            description="Select and arrange table columns as you prefer."
                            columnObj={columnObj}
                            setColumnObj={setColumnObj}
                        />
                        {/* Conditionally show Date Range box only for Custom Range */}
                        {selectedDuration === "Custom Range" && (
                            <div className="flex items-center">
                                <Box
                                    sx={(theme) => ({
                                        backgroundColor: theme.palette.selected,
                                        borderRadius: "8px",
                                        padding: theme.spacing(1),
                                    })}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        className="truncate"
                                        sx={{ fontWeight: 700, marginRight: 1 }}
                                    >
                                        <span style={{ color: "#235AF9" }}>
                                            {startDateC} - {endDateC}
                                        </span>
                                    </Typography>
                                </Box>
                            </div>
                        )}

                        <div className="relative">
                            <Button
                                onClick={handleClick}
                                variant="outlined"
                                sx={{
                                    height: { xs: "28px", sm: "30px", md: "48px" },
                                    px: { xs: 1, sm: 2 },
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: { xs: 0.5, sm: 1 },
                                    borderColor: "#5547dd",
                                    backgroundColor: "white",
                                    color: "#5547dd",
                                    minWidth: 0,
                                    maxWidth: "100%",
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <img src={calendar} alt="Calendar" width={18} height={18} />
                                    <Box sx={{ display: "block", maxWidth: "100%" }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {selectedDuration}
                                        </Typography>
                                    </Box>
                                    <img src={arrow} alt="Arrow" width={18} height={18} />
                                </Box>
                            </Button>

                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => handleClose()}
                                PaperProps={{
                                    sx: {
                                        borderRadius: "8px",
                                        top: "10px",
                                        boxShadow: "0 6px 10px rgba(0, 0, 0, 0.1)",
                                    },
                                }}
                            >
                                {dateRangeArr.map((label) => (
                                    <MenuItem
                                        key={label}
                                        onClick={() => handleClose(label)}
                                        sx={(theme) => ({
                                            transition: "all 0.2s ease-in-out",
                                            "&:hover": {
                                                backgroundColor: theme.palette.action.hover,
                                            },
                                            backgroundColor: selectedDuration === label ? theme.palette.selected : 'transparent',
                                        })}
                                    >
                                        <Typography variant="button" textTransform={"none"}>
                                            {label}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Menu>
                            <Menu
                                anchorEl={calendarAnchorEl}
                                open={showDatePicker}
                                onClose={handleCalendarClose}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            borderRadius: "8px",
                                            top: "10px",
                                            padding: "0px",
                                            boxShadow: "0 6px 10px rgba(0, 0, 0, 0.1)",
                                        },
                                    },
                                }}
                            >
                                <DateRangeFilter
                                    onCancel={handleCalendarClose}
                                    onApply={(from, to) => {
                                        setStartDate(from);
                                        setEndDate(to);
                                        setSelectedDuration("Custom Range");
                                        setShowDatePicker(false);
                                        setCalendarAnchorEl(null);
                                    }}
                                    initialFrom={startDate}
                                    initialTo={endDate}
                                />
                            </Menu>
                        </div>
                        <PermissionGuard
                            category={PermissionCategory.DOCUMENTS}
                            subcategory={PermissionSubcategory.ADD_DOCUMENT}
                        >
                            <CustomIconButton
                                icon={<img src={downloadWhiteIcon} alt="download" width={16} height={16} />}
                                text="Add Document"
                                // background="transparent"
                                variant='contained'
                                onClick={() => setIsDocumentModalOpen(!isDocumentModalOpen)}
                            />
                        </PermissionGuard>
                        <Menu
                            anchorEl={rowMenuAnchorEl}
                            open={Boolean(rowMenuAnchorEl)}
                            onClose={handleCloseMenu}
                        >
                            {viewMenuData.map((item, index) => (
                                <MenuItem
                                    key={index}
                                    onClick={item.click}
                                >
                                    <div className="flex gap-2 mt-1">
                                        <img
                                            src={item.icon}
                                            alt={item.name}
                                            style={{ marginRight: 8, width: 20, height: 20 }}
                                        />
                                        <Typography variant="body2" fontWeight={600}>
                                            {item.name}
                                        </Typography>
                                    </div>
                                </MenuItem>
                            ))}
                        </Menu>
                    </div>
                </div>
                {/* Data Table */}
                <MaterialReactTable
                    table={table}

                />
                <CreateNewDocument
                    isDocumentModalOpen={isDocumentModalOpen}
                    handleDocumentModalClose={handleDocumentModalClose}
                    onDocumentSaved={() => {
                        DeviceHealthReportRequestData({
                            startDate: startDate || startDateC,
                            endDate: endDate || endDateC,
                            fleetId: user.customer_id,
                            lastEvaluatedKey: null,
                        });
                        setCurrentPage(1);
                        paginatedArr.current = [null];
                    }}
                    categoryOptions={CategoryOptions}
                    getEntityOptions={(category: any) => {
                        switch (category) {
                            case 'VEHICLE': return VehiclesData;
                            case 'DRIVER': return DriversData;
                            case 'COMPANY': return CompanyData;
                            case 'USER': return UsersData;
                            // Add more cases as needed
                            default: return [];
                        }
                    }}
                    editMode={!!editDocumentData}
                    initialData={editDocumentData}
                />
                <DeleteDocumentData
                    isDeleteModalOpen={isDeleteModalOpen}
                    handleDeleteModalClose={handleDeleteModalClose}
                    handleDeleteDocument={deleteDocumentData}
                    heading="Delete Document"
                    message="Are you sure you want to delete this document?"
                    onDocumentSaved={() => {
                        DeviceHealthReportRequestData({
                            startDate: startDate || startDateC,
                            endDate: endDate || endDateC,
                            fleetId: user.customer_id,
                            lastEvaluatedKey: null,
                        });
                        setCurrentPage(1);
                        paginatedArr.current = [null];
                    }}
                />
                {/* MUI Pagination */}
                <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        shape="rounded"
                        color="primary"
                        siblingCount={0}
                        boundaryCount={0}
                        renderItem={(item) => {
                            if (
                                item.type === "page" &&
                                item.page !== null &&
                                item.page > currentPage
                            ) {
                                return null;
                            }
                            return <PaginationItem {...item} />;
                        }}
                    />
                </Box>
            </div>
        </>
    );
}